import React, { useEffect, useRef, useState } from 'react';
import {
  MapPinIcon,
  FolderIcon,
  ShareIcon,
  PrinterIcon,
  PaperAirplaneIcon,
  TrashIcon,
  ArrowPathIcon,
  MicrophoneIcon,
  TagIcon,
  CheckIcon,
  PhoneIcon,
  UsersIcon,
  LightBulbIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import { ChevronRightIcon, ChevronLeftIcon } from '@heroicons/react/24/solid';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

function NoteContextMenu({
  visible,
  x,
  y,
  note,
  onClose,
  onPin,
  onMove,
  onChangeType,
  onShare,
  onPrint,
  onResend,
  onResummarize,
  onRetranscribe,
  onDelete,
  secondaryEmails = [],
  primaryEmail,
}) {
  const { t } = useTranslation();
  const defaultPrimaryEmail = primaryEmail || t('common.primaryEmail');
  const menuRef = useRef(null);
  const [submenuOnLeft, setSubmenuOnLeft] = useState(false);

  const noteTypes = [
    { id: 0, label: t('notes.contextMenu.task'), icon: CheckIcon },
    { id: 1, label: t('notes.contextMenu.call'), icon: PhoneIcon },
    { id: 2, label: t('notes.contextMenu.meeting'), icon: UsersIcon },
    { id: 3, label: t('notes.contextMenu.idea'), icon: LightBulbIcon },
    { id: 4, label: t('notes.contextMenu.note'), icon: DocumentTextIcon },
  ];

  // Position menu within viewport bounds and determine submenu direction
  useEffect(() => {
    if (visible && menuRef.current) {
      const menu = menuRef.current;
      const rect = menu.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let adjustedX = x;
      let adjustedY = y;

      // Adjust if menu goes beyond right edge
      if (x + rect.width > viewportWidth) {
        adjustedX = viewportWidth - rect.width - 10;
      }

      // Adjust if menu goes beyond bottom edge
      if (y + rect.height > viewportHeight) {
        adjustedY = viewportHeight - rect.height - 10;
      }

      // Ensure menu doesn't go beyond left/top edges
      adjustedX = Math.max(10, adjustedX);
      adjustedY = Math.max(10, adjustedY);

      menu.style.left = `${adjustedX}px`;
      menu.style.top = `${adjustedY}px`;

      // Determine if submenu should open to the left
      // Check if there's enough space on the right for submenu (200px width + some margin)
      const submenuWidth = 200;
      const spaceOnRight = viewportWidth - (adjustedX + rect.width);
      setSubmenuOnLeft(spaceOnRight < submenuWidth + 20);
    }
  }, [visible, x, y]);

  // Close on outside click
  useEffect(() => {
    if (!visible) return;

    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        onClose();
      }
    };

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    const handleBlur = () => {
      onClose();
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    window.addEventListener('blur', handleBlur);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('blur', handleBlur);
    };
  }, [visible, onClose]);

  if (!visible || !note) return null;

  const handleMenuItemClick = (action) => {
    action();
    onClose();
  };

  return (
    <div
      ref={menuRef}
      className="fixed z-[70] bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[180px]"
      style={{ left: `${x}px`, top: `${y}px` }}
      role="menu"
      aria-label="Note actions"
    >
      {/* Pin/Unpin */}
      <button
        onClick={() => handleMenuItemClick(() => onPin(note))}
        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-3 transition-colors duration-150"
        role="menuitem"
        aria-label={note.isPinned ? 'Unpin note' : 'Pin note'}
      >
        <MapPinIcon className="w-4 h-4" />
        <span>{note.isPinned ? t('notes.contextMenu.unpin') : t('notes.contextMenu.pin')}</span>
      </button>

      {/* Move to Folder */}
      <button
        onClick={() => handleMenuItemClick(() => onMove(note))}
        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-3 transition-colors duration-150"
        role="menuitem"
        aria-label="Move to folder"
      >
        <FolderIcon className="w-4 h-4" />
        <span>{t('notes.contextMenu.moveToFolder')}</span>
      </button>

      {/* Change Type */}
      <div className="relative group">
        <button
          className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center justify-between transition-colors duration-150"
          role="menuitem"
          aria-label="Change note type"
        >
          <div className="flex items-center gap-3">
            <TagIcon className="w-4 h-4" />
            <span>{t('notes.contextMenu.changeType')}</span>
          </div>
          {submenuOnLeft ? (
            <ChevronLeftIcon className="w-3 h-3" />
          ) : (
            <ChevronRightIcon className="w-3 h-3" />
          )}
        </button>
        
        {/* Submenu */}
        <div 
          className={`absolute top-0 hidden group-hover:block bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[150px] z-50 ${
            submenuOnLeft 
              ? 'right-full mr-1 before:absolute before:-right-2 before:top-0 before:h-full before:w-4' 
              : 'left-full ml-1 before:absolute before:-left-2 before:top-0 before:h-full before:w-4'
          }`}
        >
          {noteTypes.map((type) => (
            <button
              key={type.id}
              onClick={() => handleMenuItemClick(() => onChangeType(note, type.id))}
              className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-3 transition-colors duration-150 ${
                note.noteType === type.id ? 'bg-orange-50 text-orange-700 font-medium' : 'text-gray-700'
              }`}
              role="menuitem"
            >
              <type.icon className={`w-4 h-4 ${note.noteType === type.id ? 'text-orange-500' : 'text-gray-400'}`} />
              <span>{type.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Share */}
      <button
        onClick={() => handleMenuItemClick(() => onShare(note))}
        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-3 transition-colors duration-150"
        role="menuitem"
        aria-label="Share note"
      >
        <ShareIcon className="w-4 h-4" />
        <span>{t('notes.contextMenu.share')}</span>
      </button>

      {/* Print */}
      <button
        onClick={() => handleMenuItemClick(() => onPrint(note))}
        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-3 transition-colors duration-150"
        role="menuitem"
        aria-label="Print note"
      >
        <PrinterIcon className="w-4 h-4" />
        <span>{t('notes.contextMenu.print')}</span>
      </button>

      {onResummarize && (
        <button
          onClick={() => handleMenuItemClick(() => onResummarize(note))}
          className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-3 transition-colors duration-150"
          role="menuitem"
          aria-label={t('notes.contextMenu.summarizeAgain')}
        >
          <ArrowPathIcon className="w-4 h-4" />
          <span>{t('notes.contextMenu.summarizeAgain')}</span>
        </button>
      )}

      {onRetranscribe && note.recordingURL && (
        <button
          onClick={() => handleMenuItemClick(() => onRetranscribe(note))}
          className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-3 transition-colors duration-150"
          role="menuitem"
          aria-label={t('notes.contextMenu.transcribeAgain')}
        >
          <MicrophoneIcon className="w-4 h-4" />
          <span>{t('notes.contextMenu.transcribeAgain')}</span>
        </button>
      )}

      {/* Resend by Email */}
      {secondaryEmails.length > 0 ? (
        <div className="relative group">
          <button
            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center justify-between transition-colors duration-150"
            role="menuitem"
            aria-label="Resend by email"
          >
            <div className="flex items-center gap-3">
              <PaperAirplaneIcon className="w-4 h-4" />
              <span>{t('notes.contextMenu.resendByEmail')}</span>
            </div>
            {submenuOnLeft ? (
              <ChevronLeftIcon className="w-3 h-3" />
            ) : (
              <ChevronRightIcon className="w-3 h-3" />
            )}
          </button>
          
          {/* Submenu - shows on left or right depending on available space */}
          <div 
            className={`absolute top-0 hidden group-hover:block bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[200px] z-50 ${
              submenuOnLeft 
                ? 'right-full mr-1 before:absolute before:-right-2 before:top-0 before:h-full before:w-4' 
                : 'left-full ml-1 before:absolute before:-left-2 before:top-0 before:h-full before:w-4'
            }`}
          >
            {/* Primary Email */}
            <button
              onClick={() => handleMenuItemClick(() => onResend(note, null))}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-150 truncate"
              role="menuitem"
            >
              {defaultPrimaryEmail}
            </button>
            
            {/* Secondary Emails */}
            {secondaryEmails.map((email) => (
              <button
                key={email}
                onClick={() => handleMenuItemClick(() => onResend(note, email))}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-150 truncate"
                role="menuitem"
              >
                {email}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <button
          onClick={() => handleMenuItemClick(() => onResend(note, null))}
          className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-3 transition-colors duration-150"
          role="menuitem"
          aria-label="Resend by email"
        >
          <PaperAirplaneIcon className="w-4 h-4" />
          <span>{t('notes.contextMenu.resendByEmail')}</span>
        </button>
      )}

      <div className="border-t border-gray-200 my-1" />

      {/* Delete */}
      <button
        onClick={() => handleMenuItemClick(() => onDelete(note))}
        className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-3 transition-colors duration-150"
        role="menuitem"
        aria-label="Delete note"
      >
        <TrashIcon className="w-4 h-4" />
        <span>{t('notes.contextMenu.delete')}</span>
      </button>
    </div>
  );
}

NoteContextMenu.propTypes = {
  visible: PropTypes.bool.isRequired,
  x: PropTypes.number.isRequired,
  y: PropTypes.number.isRequired,
  note: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onPin: PropTypes.func.isRequired,
  onMove: PropTypes.func.isRequired,
  onChangeType: PropTypes.func.isRequired,
  onShare: PropTypes.func.isRequired,
  onPrint: PropTypes.func.isRequired,
  onResend: PropTypes.func.isRequired,
  onResummarize: PropTypes.func,
  onRetranscribe: PropTypes.func,
  onDelete: PropTypes.func.isRequired,
  secondaryEmails: PropTypes.arrayOf(PropTypes.string),
  primaryEmail: PropTypes.string,
};

export default NoteContextMenu;

